'use client';

import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { IS_MOBILE } from '@repo/common-utils';
import { cva, type VariantProps } from 'class-variance-authority';
import { ComponentPropsWithoutRef, ElementRef, forwardRef, ReactNode, useState, useCallback } from 'react';

import { cn } from '../../libs/utils';

const TooltipProvider = TooltipPrimitive.Provider;
const TooltipRoot = TooltipPrimitive.Root;
const TooltipTrigger = TooltipPrimitive.Trigger;
const TooltipPortal = TooltipPrimitive.Portal;
const TooltipArrow = TooltipPrimitive.Arrow;

const tooltipShellVariants = cva([
  'animate-in fade-in-0 zoom-in-95',
  'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
  'data-[side=bottom]:slide-in-from-top-2',
  'data-[side=left]:slide-in-from-right-2',
  'data-[side=right]:slide-in-from-left-2',
  'data-[side=top]:slide-in-from-bottom-2',
  'max-w-[calc(100vw-24px)]'
]);

const tooltipBodyVariants = cva(
  ['rounded-bzc-xsmall bg-bzc-gray-alpha-800', 'text-neutral-foreground-inverse-main elevation-medium select-none', 'w-full break-all'],
  {
    variants: {
      size: {
        medium: 'py-tiny-large px-normal-medium caption-medium-semibold',
        small: 'py-tiny-large px-normal-small caption-xsmall-semibold'
      }
    },
    defaultVariants: {
      size: 'medium'
    }
  }
);

interface TooltipContentProps extends ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>, VariantProps<typeof tooltipBodyVariants> {}

const TooltipContent = forwardRef<ElementRef<typeof TooltipPrimitive.Content>, TooltipContentProps>(({ className, sideOffset, size, ...props }, ref) => (
  <TooltipPrimitive.Content
    ref={ref}
    sideOffset={sideOffset}
    avoidCollisions={true}
    collisionPadding={8}
    className={cn(tooltipShellVariants(), className)}
    {...props}
  />
));
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

interface TooltipProps extends Omit<ComponentPropsWithoutRef<typeof TooltipPrimitive.Root>, 'children'> {
  children: ReactNode;
  content: ReactNode;
  size?: VariantProps<typeof tooltipBodyVariants>['size'];
  side?: ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>['side'];
  sideOffset?: number;
  className?: string;
  contentClassName?: string;
  zIndex?: number;
}

const Tooltip = forwardRef<HTMLButtonElement, TooltipProps>(
  (
    {
      children,
      content,
      open: openProp,
      defaultOpen,
      onOpenChange,
      delayDuration = 400,
      side = 'top',
      size = 'medium',
      sideOffset,
      className,
      contentClassName,
      zIndex = 50,
      ...props
    },
    ref
  ) => {
    const [isOpen, setIsOpen] = useState(defaultOpen ?? false);

    const sideOffsetValue = sideOffset ?? (size === 'small' ? 4 : 8);

    const isControlled = openProp !== undefined;
    const currentOpen = isControlled ? openProp : isOpen;

    const handleOpenChange = useCallback(
      (newOpen: boolean) => {
        if (!isControlled) {
          setIsOpen(newOpen);
        }
        onOpenChange?.(newOpen);
      },
      [isControlled, onOpenChange]
    );

    const mobileTriggerProps = IS_MOBILE
      ? {
          onPointerDown: (e: React.PointerEvent) => {
            e.preventDefault();
          },
          onClick: (e: React.MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();
            handleOpenChange(!currentOpen);
          }
        }
      : {};

    return (
      <TooltipRoot delayDuration={IS_MOBILE ? 0 : delayDuration} open={currentOpen} onOpenChange={handleOpenChange} {...props}>
        <TooltipTrigger ref={ref} asChild className={className} {...mobileTriggerProps}>
          {children}
        </TooltipTrigger>

        <TooltipPortal>
          <TooltipContent side={side} size={size} sideOffset={sideOffsetValue} style={{ zIndex }}>
            <div className={cn(tooltipBodyVariants({ size }), contentClassName)}>{content}</div>
            <TooltipArrow className="fill-bzc-gray-alpha-800" />
          </TooltipContent>
        </TooltipPortal>
      </TooltipRoot>
    );
  }
);

Tooltip.displayName = 'Tooltip';

export { Tooltip, TooltipProvider, TooltipRoot, TooltipTrigger, TooltipContent, TooltipPortal, TooltipArrow };
export type { TooltipProps };
